import { WorkCalendar } from "./calendar";
import { toDay } from "./date";
import { buildTree } from "./cpm";
import type { Baseline, Project, Schedule } from "./types";

export type HealthSeverity = "error" | "warning" | "info";

export interface HealthIssue {
  code:
    | "cycle"
    | "invalid-dependency"
    | "no-predecessor"
    | "no-successor"
    | "rule-8-80"
    | "negative-float"
    | "hard-constraint"
    | "deadline-missed"
    | "long-lag"
    | "single-child"
    | "weight-sum"
    | "no-baseline"
    | "behind-status"
    | "not-started"
    | "empty-name";
  severity: HealthSeverity;
  title: string;
  detail: string;
  taskIds: string[];
}

export interface HealthReport {
  score: number; // 0..100
  issues: HealthIssue[];
  counts: Record<HealthSeverity, number>;
}

export function analyzeHealth(project: Project, schedule: Schedule): HealthReport {
  const issues: HealthIssue[] = [];
  const tree = buildTree(project.tasks);
  const preds = new Map<string, number>();
  const succs = new Map<string, number>();
  for (const d of project.dependencies) {
    succs.set(d.predecessorId, (succs.get(d.predecessorId) ?? 0) + 1);
    preds.set(d.successorId, (preds.get(d.successorId) ?? 0) + 1);
    // dependency on an ancestor counts for its descendants
    for (const leaf of tree.leaves.get(d.successorId) ?? []) if (leaf !== d.successorId) preds.set(leaf, (preds.get(leaf) ?? 0) + 1);
    for (const leaf of tree.leaves.get(d.predecessorId) ?? []) if (leaf !== d.predecessorId) succs.set(leaf, (succs.get(leaf) ?? 0) + 1);
  }

  for (const e of schedule.errors) {
    issues.push({
      code: e.code === "cycle" ? "cycle" : "invalid-dependency",
      severity: "error",
      title: e.code === "cycle" ? "순환 종속성" : "무효한 관계",
      detail: e.message,
      taskIds: e.taskIds,
    });
  }

  const leaves = schedule.order.filter((id) => !schedule.tasks.get(id)!.isSummary);
  const statusDay = toDay(project.statusDate);

  const noPred: string[] = [];
  const noSucc: string[] = [];
  const rule880: string[] = [];
  const negFloat: string[] = [];
  const hard: string[] = [];
  const deadline: string[] = [];
  const behind: string[] = [];
  const notStarted: string[] = [];
  const emptyName: string[] = [];

  for (const id of leaves) {
    const t = project.tasks.find((x) => x.id === id)!;
    const s = schedule.tasks.get(id)!;
    if (!t.name.trim()) emptyName.push(id);
    if (!(preds.get(id) ?? 0) && (!t.constraint || t.constraint.auto) && id !== leaves[0]) noPred.push(id);
    if (!(succs.get(id) ?? 0) && s.finish !== schedule.projectFinish) noSucc.push(id);
    if (s.duration > 10) rule880.push(id);
    if (s.totalFloat < 0) negFloat.push(id);
    if (t.constraint?.type === "MSO") hard.push(id);
    if (s.deadlineMissed) deadline.push(id);
    if (toDay(s.finish) < statusDay && s.percentComplete < 100) behind.push(id);
    else if (toDay(s.start) < statusDay && s.percentComplete === 0) notStarted.push(id);
  }
  const longLag = project.dependencies.filter((d) => Math.abs(d.lag) > 5).map((d) => d.successorId);

  const singleChild: string[] = [];
  const weightSum: string[] = [];
  for (const [pid, kids] of tree.children) {
    if (pid === null) continue;
    if (kids.length === 1) singleChild.push(pid);
    const weights = kids.map((k) => tree.byId.get(k)!.weight).filter((w): w is number => typeof w === "number");
    if (weights.length === kids.length && kids.length > 0) {
      const sum = weights.reduce((a, b) => a + b, 0);
      if (Math.abs(sum - 100) > 0.01) weightSum.push(pid);
    }
  }

  const push = (issue: Omit<HealthIssue, "taskIds"> & { taskIds: string[] }) => {
    if (issue.taskIds.length) issues.push(issue);
  };
  push({
    code: "negative-float",
    severity: "error",
    title: "날짜를 맞출 수 없음",
    detail: `${negFloat.length}개 작업이 마감일이나 고정 날짜에 맞춰 끝날 수 없습니다. 기간을 줄이거나 날짜를 바꿔 주세요.`,
    taskIds: negFloat,
  });
  push({
    code: "deadline-missed",
    severity: "error",
    title: "마감일 넘김",
    detail: `${deadline.length}개 작업이 마감일보다 늦게 끝납니다.`,
    taskIds: deadline,
  });
  push({
    code: "behind-status",
    severity: "warning",
    title: "늦어진 작업",
    detail: `${behind.length}개 작업이 진척 기준일까지 끝났어야 하는데 아직 끝나지 않았습니다.`,
    taskIds: behind,
  });
  push({
    code: "no-predecessor",
    severity: "warning",
    title: "앞 작업이 없음",
    detail: `${noPred.length}개 작업이 무엇 다음에 오는지 정해져 있지 않습니다. 앞 작업이 늦어져도 이 작업은 밀리지 않아 종료일이 실제보다 이르게 나옵니다.`,
    taskIds: noPred,
  });
  push({
    code: "no-successor",
    severity: "warning",
    title: "다음 작업이 없음",
    detail: `${noSucc.length}개 작업 뒤에 이어지는 작업이 없습니다. 이 작업이 늦어져도 플랜 종료일에 반영되지 않습니다.`,
    taskIds: noSucc,
  });
  push({
    code: "rule-8-80",
    severity: "warning",
    title: "너무 긴 작업",
    detail: `${rule880.length}개 작업이 2주(10근무일)를 넘습니다. 작게 나누면 진행 상황을 정확히 볼 수 있습니다.`,
    taskIds: rule880,
  });
  push({
    code: "hard-constraint",
    severity: "warning",
    title: "날짜가 고정된 작업",
    detail: `${hard.length}개 작업의 시작일이 고정되어 있습니다. 앞 작업이 늦어져도 이 작업은 움직이지 않아 실제와 다를 수 있습니다.`,
    taskIds: hard,
  });
  push({
    code: "not-started",
    severity: "info",
    title: "시작하지 않은 작업",
    detail: `${notStarted.length}개 작업이 계획대로면 이미 시작했어야 하는데 진척이 0%입니다.`,
    taskIds: notStarted,
  });
  push({
    code: "long-lag",
    severity: "info",
    title: "긴 대기 시간",
    detail: `${longLag.length}개 연결에서 앞 작업이 끝나고 다음 작업까지 5일 넘게 기다립니다. 그 사이에 빠진 작업이 없는지 확인해 주세요.`,
    taskIds: longLag,
  });
  push({
    code: "single-child",
    severity: "info",
    title: "하위 작업이 하나뿐",
    detail: `${singleChild.length}개 단계에 하위 작업이 1개만 있습니다. 나눌 필요가 없다면 한 작업으로 합쳐도 됩니다.`,
    taskIds: singleChild,
  });
  push({
    code: "weight-sum",
    severity: "warning",
    title: "가중치 합이 100%가 아님",
    detail: `${weightSum.length}개 단계에서 하위 작업 가중치를 더하면 100%가 되지 않습니다.`,
    taskIds: weightSum,
  });
  push({ code: "empty-name", severity: "info", title: "이름 없는 작업", detail: `${emptyName.length}개 작업에 이름이 없습니다.`, taskIds: emptyName });
  if (!project.baselines.length) {
    issues.push({
      code: "no-baseline",
      severity: "info",
      title: "기준선 없음",
      detail: "처음 계획을 저장해 두지 않으면 얼마나 늦어졌는지 알 수 없습니다. 계획이 정해지면 툴바의 기준선 › 저장을 눌러 주세요.",
      taskIds: [],
    });
  }

  const counts: Record<HealthSeverity, number> = { error: 0, warning: 0, info: 0 };
  for (const i of issues) counts[i.severity] += i.taskIds.length || 1;
  const leafCount = Math.max(1, leaves.length);
  const penalty = (counts.error * 6 + counts.warning * 2 + counts.info * 0.5) / leafCount;
  const score = Math.max(0, Math.min(100, Math.round(100 - penalty * 25)));
  return { score, issues, counts };
}

// ---------------- EVM (schedule-only, duration as value proxy) ----------------

export interface EvmSummary {
  hasBaseline: boolean;
  baselineName?: string;
  pv: number; // planned value 0..1 of total baseline duration
  ev: number;
  spi: number | null;
  /** schedule variance in working days (negative = behind) */
  svDays: number;
  baselineFinish?: string;
  finishVarianceDays: number;
  /** cumulative planned-value S-curve (0..1), sampled every 5 working days */
  curve: Array<{ day: number; date: string; pv: number }>;
  /** working days from project start to the status date */
  statusDay: number;
}

export function computeEvm(project: Project, schedule: Schedule, baseline: Baseline | null, calendar?: WorkCalendar): EvmSummary {
  const cal = calendar ?? new WorkCalendar(project.calendar);
  if (!baseline) {
    return { hasBaseline: false, pv: 0, ev: 0, spi: null, svDays: 0, finishVarianceDays: 0, curve: [], statusDay: 0 };
  }
  const statusOrd = cal.ordinalBeforeISO(project.statusDate) + 1; // half-open: work through status date
  const leaves = schedule.order.filter((id) => !schedule.tasks.get(id)!.isSummary);
  let total = 0;
  let pv = 0;
  let ev = 0;
  const items: Array<{ s: number; f: number; w: number; pct: number }> = [];
  for (const id of leaves) {
    const b = baseline.tasks[id];
    const cur = schedule.tasks.get(id)!;
    if (!b) continue;
    const w = Math.max(b.duration, 0.5); // milestones get a small weight
    total += w;
    const s = cal.ordinalOfISO(b.start);
    const f = b.duration > 0 ? s + b.duration : s + 1;
    items.push({ s, f, w, pct: cur.percentComplete });
    const planned = Math.max(0, Math.min(1, (statusOrd - s) / (f - s)));
    pv += w * planned;
    ev += w * (cur.percentComplete / 100);
  }
  const spi = pv > 0 ? ev / pv : null;
  const bFinish = cal.ordinalBeforeISO(baseline.projectFinish);
  const cFinish = cal.ordinalBeforeISO(schedule.projectFinish);
  const projectStartOrd = cal.ordinalOfISO(project.start);
  // curve: weekly samples from project start to max(baseline finish, current finish)
  const endOrd = Math.max(bFinish, cFinish) + 1;
  const curve: EvmSummary["curve"] = [];
  const step = 5;
  for (let o = projectStartOrd; o <= endOrd; o += step) {
    let cpv = 0;
    for (const it of items) cpv += it.w * Math.max(0, Math.min(1, (o - it.s) / (it.f - it.s)));
    curve.push({ day: o - projectStartOrd, date: cal.isoOf(o), pv: total ? cpv / total : 0 });
  }
  return {
    statusDay: statusOrd - projectStartOrd,
    hasBaseline: true,
    baselineName: baseline.name,
    pv: total ? pv / total : 0,
    ev: total ? ev / total : 0,
    spi,
    svDays: total ? Math.round(((ev - pv) / total) * (bFinish - projectStartOrd + 1)) : 0,
    baselineFinish: baseline.projectFinish,
    finishVarianceDays: cFinish - bFinish,
    curve,
  };
}

export interface DelayItem {
  taskId: string;
  name: string;
  wbs: string;
  baselineFinish: string;
  finish: string;
  /** working days late vs baseline (positive = late) */
  varianceDays: number;
  critical: boolean;
}

export function topDelays(project: Project, schedule: Schedule, baseline: Baseline | null, limit = 5, calendar?: WorkCalendar): DelayItem[] {
  if (!baseline) return [];
  const cal = calendar ?? new WorkCalendar(project.calendar);
  const items: DelayItem[] = [];
  for (const id of schedule.order) {
    const s = schedule.tasks.get(id)!;
    if (s.isSummary) continue;
    const b = baseline.tasks[id];
    if (!b) continue;
    const v = cal.ordinalBeforeISO(s.finish) - cal.ordinalBeforeISO(b.finish);
    if (v > 0) {
      const t = project.tasks.find((x) => x.id === id)!;
      items.push({ taskId: id, name: t.name, wbs: s.wbs, baselineFinish: b.finish, finish: s.finish, varianceDays: v, critical: s.critical });
    }
  }
  return items.sort((a, b) => b.varianceDays - a.varianceDays || Number(b.critical) - Number(a.critical)).slice(0, limit);
}

export function createBaseline(project: Project, schedule: Schedule, name: string, note?: string): Baseline {
  const tasks: Baseline["tasks"] = {};
  for (const [id, s] of schedule.tasks) {
    tasks[id] = { start: s.start, finish: s.finish, duration: s.duration, percentComplete: s.percentComplete };
  }
  return {
    id: `bl_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
    name,
    note,
    createdAt: new Date().toISOString(),
    projectFinish: schedule.projectFinish,
    tasks,
  };
}
